import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import {
    Box, Button, ButtonGroup, Flex, Grid, Input, Modal, ModalBody,
    ModalCloseButton, ModalContent, ModalHeader, ModalOverlay,
    NumberInput, NumberInputField, Radio, RadioGroup, Select,
    Spinner, Stack, Text, useColorModeValue, useDisclosure, useToast
} from "@chakra-ui/react"
import { useQuery as useQueryHook } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Tag } from "components"
import { API } from "config"
import { useAllDistributors } from "hooks"
import { Authenticate } from "screens"

// ─── FieldLabel ───────────────────────────────────────────────────────────────

const FieldLabel = ({ children, required }) => (
    <Box mb={1}>
        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider"
            color="gray.500" _dark={{ color: 'gray.400' }}>
            {children}{required && <Box as="span" color="red.400" ml={0.5}>*</Box>}
        </Text>
    </Box>
)

// ─── Hook: lotes de um produto num núcleo ─────────────────────────────────────

const useProductLots = (productId, distributorId) => useQueryHook({
    queryKey: ['stock-lots', productId, distributorId],
    queryFn: async () => {
        if (!productId || !distributorId) return []
        const { data } = await API.get(`/stock/lots?product_id=${productId}&distributor_id=${distributorId}`)
        return data?.data ?? []
    },
    enabled: !!(productId && distributorId),
    staleTime: 15000,
})

// ─── Modal de seleção de lote ─────────────────────────────────────────────────

const LotSelectModal = ({ isOpen, onClose, productName, lots, isFetching, onSelect }) => {
    const [selected, setSelected] = useState('')
    const lotBorder = useColorModeValue('gray.100', 'gray.700')
    const lotBg     = useColorModeValue('gray.50',  'gray.750')
    const selBorder = useColorModeValue('teal.300', 'teal.500')
    const selBg     = useColorModeValue('teal.50',  'teal.900')

    useEffect(() => { if (isOpen) setSelected('') }, [isOpen])

    const handleConfirm = () => {
        const lot = lots.find(l => l.lot_number === selected)
        onSelect(lot ?? null)
        onClose()
    }

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—'

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
            <ModalOverlay backdropFilter="blur(2px)" />
            <ModalContent borderRadius="2xl" overflow="hidden">
                <ModalHeader fontSize="md" fontWeight="700" pb={2}>
                    Selecionar Lote
                    <Text fontSize="xs" fontWeight="normal" color="gray.500" mt={0.5}>
                        {productName}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    {isFetching ? (
                        <Flex justify="center" py={8}><Spinner size="md" color="teal.400" /></Flex>
                    ) : lots.length === 0 ? (
                        <Flex direction="column" align="center" gap={2} py={8} color="gray.400">
                            <Icon name="stock" boxSize={7} />
                            <Text fontSize="sm">Nenhum lote disponível para este produto neste núcleo.</Text>
                            <Button mt={2} size="sm" variant="outline" colorScheme="gray" onClick={() => { onSelect(null); onClose() }}>
                                Transferir sem lote
                            </Button>
                        </Flex>
                    ) : (
                        <>
                            <Text fontSize="xs" color="gray.500" mb={3}>
                                Selecione o lote de origem para esta transferência.
                            </Text>
                            <RadioGroup value={selected} onChange={setSelected}>
                                <Stack spacing={2}>
                                    {lots.map(lot => (
                                        <Box
                                            key={lot.lot_number}
                                            as="label"
                                            cursor="pointer"
                                            border="1.5px solid"
                                            borderColor={selected === lot.lot_number ? selBorder : lotBorder}
                                            bg={selected === lot.lot_number ? selBg : lotBg}
                                            borderRadius="xl" px={4} py={3}
                                            transition="all 0.15s"
                                            _hover={{ borderColor: 'teal.300' }}
                                        >
                                            <Radio value={lot.lot_number} w="100%">
                                                <Flex align="center" justify="space-between" flex={1} ml={2}>
                                                    <Box>
                                                        <Flex align="center" gap={2}>
                                                            <Text fontFamily="mono" fontSize="sm" fontWeight="700">
                                                                {lot.lot_number}
                                                            </Text>
                                                            {lot.is_provisional && (
                                                                <Tag colorScheme="purple" size="sm">Provisório</Tag>
                                                            )}
                                                        </Flex>
                                                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                                                            Validade: {fmtDate(lot.expires_at)}
                                                        </Text>
                                                    </Box>
                                                    <Box textAlign="right">
                                                        <Text fontSize="lg" fontWeight="bold" color="teal.500" lineHeight="1">
                                                            {lot.quantity}
                                                        </Text>
                                                        <Text fontSize="2xs" color="gray.400">un. disponíveis</Text>
                                                    </Box>
                                                </Flex>
                                            </Radio>
                                        </Box>
                                    ))}
                                </Stack>
                            </RadioGroup>

                            <Flex gap={3} mt={5} justify="flex-end">
                                <Button size="sm" variant="ghost" onClick={() => { onSelect(null); onClose() }}>
                                    Sem lote específico
                                </Button>
                                <Button size="sm" colorScheme="teal" isDisabled={!selected} onClick={handleConfirm}>
                                    Confirmar lote
                                </Button>
                            </Flex>
                        </>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

// ─── ProductSearch ────────────────────────────────────────────────────────────

const ProductSearch = ({ value, label, onSelect }) => {
    const [term, setTerm]               = useState(label || '')
    const [debouncedTerm, setDebounced] = useState('')
    const [open, setOpen]               = useState(false)
    const wrapRef = useRef()

    useEffect(() => { if (!value) { setTerm(''); setDebounced('') } }, [value])

    useEffect(() => {
        if (!term || term === label) return
        const t = setTimeout(() => setDebounced(term), 350)
        return () => clearTimeout(t)
    }, [term, label])

    const { data: products, isFetching } = useQueryHook({
        queryKey: ['product-search-transfer', debouncedTerm],
        queryFn: async () => {
            const res = await API.get(`/products?term=${debouncedTerm}`)
            return Array.isArray(res.data) ? res.data : res.data?.data ?? []
        },
        enabled: debouncedTerm.length >= 2,
        staleTime: 30000,
    })

    const handleSelect = (p) => { onSelect(p); setTerm(p.name); setOpen(false) }
    const handleClear  = () => { onSelect(null); setTerm(''); setDebounced('') }

    if (value) {
        return (
            <Flex align="center" border="1.5px solid" borderColor="teal.300"
                bg="teal.50" _dark={{ bg: 'teal.900', borderColor: 'teal.500' }}
                borderRadius="md" px={3} gap={2} h="40px"
            >
                <Icon name="product" boxSize={3.5} color="teal.500" />
                <Text fontSize="xs" flex={1} noOfLines={1} fontWeight="semibold"
                    color="teal.700" _dark={{ color: 'teal.200' }}>
                    {label || value}
                </Text>
                <IconButton icon="cancel" size="xs" variant="ghost" colorScheme="red"
                    aria-label="Remover produto" onClick={handleClear} />
            </Flex>
        )
    }

    return (
        <Box position="relative" ref={wrapRef}>
            <Input size="md" value={term} placeholder="Buscar produto..."
                onChange={e => { setTerm(e.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 180)}
            />
            {open && term.length >= 2 && (
                <Box
                    position="absolute" top="calc(100% + 4px)" left={0} right={0}
                    bg="white" border="1px solid" borderColor="gray.200"
                    _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                    borderRadius="md" boxShadow="xl" zIndex={2000} maxH="220px" overflowY="auto"
                >
                    {isFetching && <Flex justify="center" py={3}><Spinner size="sm" /></Flex>}
                    {!isFetching && (!products || products.length === 0) && (
                        <Text fontSize="sm" color="gray.500" px={4} py={3}>Nenhum produto encontrado</Text>
                    )}
                    {(products ?? []).slice(0, 10).map(p => (
                        <Box key={p.id} px={4} py={2} cursor="pointer"
                            _hover={{ bg: 'teal.50' }} _dark={{ _hover: { bg: 'gray.700' } }}
                            onMouseDown={() => handleSelect(p)}
                        >
                            <Text fontSize="sm" fontWeight="semibold">{p.name}</Text>
                            {p.cod && <Text fontSize="xs" color="gray.400">Cód: {p.cod}</Text>}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ─── Item de transferência ────────────────────────────────────────────────────

const TransferItem = ({ item, index, fromId, canRemove, onChange, onRemove, errors }) => {
    const lotModal   = useDisclosure()
    const cardBg     = useColorModeValue('white', 'gray.800')
    const lotBorder  = useColorModeValue('purple.200', 'purple.700')
    const lotBg      = useColorModeValue('purple.50', 'purple.900')

    const { data: lots = [], isFetching: loadingLots } = useProductLots(item.product_id, fromId)

    const hasLots = lots.length > 0

    const handleProductSelect = (p) => {
        onChange({ product_id: p?.id ?? '', product_name: p?.name ?? '', lot_number: '', lot_quantity: null })
    }

    const handleLotSelect = (lot) => {
        onChange({ lot_number: lot?.lot_number ?? '', lot_quantity: lot?.quantity ?? null })
    }

    return (
        <Box
            border="1.5px solid"
            borderColor={item.product_id ? 'teal.400' : 'gray.200'}
            _dark={{ borderColor: item.product_id ? 'teal.600' : 'gray.600' }}
            borderRadius="xl" transition="border-color 0.2s"
        >
            {/* Header do item */}
            <Flex align="center" gap={3} px={5} py={3} borderTopRadius="xl"
                bgGradient={item.product_id
                    ? 'linear(to-r, teal.700, teal.800)'
                    : 'linear(to-r, gray.600, gray.700)'}
            >
                <Flex align="center" justify="center" w={7} h={7} borderRadius="full" flexShrink={0}
                    bg="white" fontSize="xs" fontWeight="900"
                    color={item.product_id ? 'teal.600' : 'gray.600'}>
                    {index + 1}
                </Flex>
                <Text fontSize="sm" fontWeight="700" color="white" flex={1} noOfLines={1}>
                    {item.product_name || `Produto ${index + 1}`}
                </Text>
                {item.lot_number && (
                    <Tag colorScheme="purple" size="sm" fontFamily="mono" mr={1}>
                        Lote: {item.lot_number}
                    </Tag>
                )}
                {canRemove && (
                    <Box as="button" onClick={onRemove} lineHeight={0} p={1.5}
                        borderRadius="md" color="whiteAlpha.600"
                        _hover={{ color: 'red.300', bg: 'blackAlpha.300' }}
                        transition="all 0.15s"
                    >
                        <Icon name="cancel" boxSize={4} />
                    </Box>
                )}
            </Flex>

            <Box p={5} bg={cardBg} borderBottomRadius="xl">
                <Grid templateColumns={{ base: '1fr', md: '1fr 140px' }} gap={4} alignItems="flex-end" mb={item.product_id && hasLots ? 3 : 0}>
                    <Box>
                        <FieldLabel required>Produto</FieldLabel>
                        <ProductSearch
                            value={item.product_id}
                            label={item.product_name}
                            onSelect={handleProductSelect}
                        />
                        {errors?.product && <Text fontSize="2xs" color="red.500" mt={0.5}>{errors.product}</Text>}
                    </Box>
                    <Box>
                        <FieldLabel required>Quantidade</FieldLabel>
                        <NumberInput size="md" min={1}
                            max={item.lot_quantity ?? undefined}
                            value={item.quantity}
                            onChange={v => onChange({ quantity: Number(v) || 1 })}
                            isInvalid={!!errors?.qty}
                        >
                            <NumberInputField placeholder="1" />
                        </NumberInput>
                        {errors?.qty && <Text fontSize="2xs" color="red.500" mt={0.5}>{errors.qty}</Text>}
                    </Box>
                </Grid>

                {/* Seção de lote — visível quando produto selecionado e fromId definido */}
                {item.product_id && fromId && (
                    <Box
                        border="1px dashed"
                        borderColor={item.lot_number ? lotBorder : 'gray.300'}
                        bg={item.lot_number ? lotBg : 'transparent'}
                        _dark={{ borderColor: item.lot_number ? 'purple.600' : 'gray.600' }}
                        borderRadius="lg" px={4} py={3} mt={3}
                    >
                        <Flex align="center" justify="space-between">
                            <Box>
                                <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                                    letterSpacing="wider" color="gray.500" _dark={{ color: 'gray.400' }}>
                                    Lote de Origem
                                </Text>
                                {item.lot_number ? (
                                    <Flex align="center" gap={2} mt={1}>
                                        <Text fontFamily="mono" fontSize="sm" fontWeight="700"
                                            color="purple.600" _dark={{ color: 'purple.300' }}>
                                            {item.lot_number}
                                        </Text>
                                        {item.lot_quantity != null && (
                                            <Text fontSize="xs" color="gray.500">
                                                ({item.lot_quantity} un. disponíveis)
                                            </Text>
                                        )}
                                    </Flex>
                                ) : (
                                    <Text fontSize="xs" color="gray.400" mt={0.5}>
                                        {loadingLots ? 'Verificando lotes...' : hasLots ? `${lots.length} lote(s) disponível(is)` : 'Sem lotes registrados'}
                                    </Text>
                                )}
                            </Box>
                            <Button size="sm" variant={item.lot_number ? 'outline' : 'solid'}
                                colorScheme={item.lot_number ? 'gray' : 'purple'}
                                isLoading={loadingLots}
                                onClick={lotModal.onOpen}
                                isDisabled={!fromId}
                            >
                                {item.lot_number ? 'Trocar lote' : 'Selecionar lote'}
                            </Button>
                        </Flex>
                    </Box>
                )}
            </Box>

            <LotSelectModal
                isOpen={lotModal.isOpen}
                onClose={lotModal.onClose}
                productName={item.product_name}
                lots={lots}
                isFetching={loadingLots}
                onSelect={handleLotSelect}
            />
        </Box>
    )
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function Transfers() {
    const navigate      = useNavigate()
    const toast         = useToast()
    const distributors  = useAllDistributors()
    const warehouses    = distributors.data?.filter(d => d.is_warehouse) ?? []

    const cardBg     = useColorModeValue('white', 'gray.800')
    const cardBorder = useColorModeValue('gray.200', 'gray.700')
    const labelColor = useColorModeValue('gray.500', 'gray.400')

    const authenticate = useDisclosure()
    const [loading, setLoading] = useState(false)
    const [fromId, setFromId]   = useState('')
    const [toId, setToId]       = useState('')
    const [notes, setNotes]     = useState('')
    const [errors, setErrors]   = useState({})

    const [items, setItems] = useState([
        { product_id: '', product_name: '', quantity: 1, lot_number: '', lot_quantity: null },
    ])

    const warehouseOptions = [
        { label: 'Selecione um núcleo', value: '' },
        ...warehouses.map(d => ({ label: d.name, value: d.id })),
    ]

    const addItem = () => setItems(prev => [
        ...prev, { product_id: '', product_name: '', quantity: 1, lot_number: '', lot_quantity: null }
    ])
    const rmItem  = (i) => setItems(prev => prev.filter((_, idx) => idx !== i))
    const setItem = (i, patch) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it))

    // Limpar lotes ao trocar a origem
    const handleFromChange = (v) => {
        setFromId(v)
        setItems(prev => prev.map(it => ({ ...it, lot_number: '', lot_quantity: null })))
        setErrors(e => ({ ...e, from: null }))
    }

    const validate = () => {
        const e = {}
        if (!fromId) e.from = 'Selecione o núcleo de origem'
        if (!toId)   e.to   = 'Selecione o núcleo de destino'
        if (fromId && toId && fromId === toId) e.to = 'Origem e destino devem ser diferentes'
        items.forEach((it, i) => {
            if (!it.product_id) e[`item_${i}_product`] = 'Selecione um produto'
            if (!it.quantity || it.quantity < 1) e[`item_${i}_qty`] = 'Quantidade inválida'
        })
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) return
        setLoading(true)
        try {
            await API.post('/stock/transfers', {
                from_distributor_id: fromId,
                to_distributor_id:   toId,
                notes:               notes || undefined,
                items: items.map(it => ({
                    product_id: it.product_id,
                    quantity:   Number(it.quantity),
                    ...(it.lot_number && { lot_number: it.lot_number }),
                })),
            })
            toast({ title: 'Transferência realizada com sucesso', status: 'success', duration: 4000 })
            setFromId('')
            setToId('')
            setNotes('')
            setErrors({})
            setItems([{ product_id: '', product_name: '', quantity: 1, lot_number: '', lot_quantity: null }])
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'Erro ao realizar transferência'
            toast({ title: msg, status: 'error', duration: 6000 })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Transferência de Estoque">
                <ButtonGroup size="sm">
                    <IconButton icon="left" label="Voltar" onClick={() => navigate('/stock/movements')} />
                </ButtonGroup>
            </Heading>

            <Text color={labelColor} fontSize="sm" mt={1} mb={6}>
                Mova produtos entre núcleos de estoque. Gera saída na origem e entrada no destino — ambas registradas no histórico de movimentações.
            </Text>

            {/* Núcleos */}
            <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" p={6} mb={5}>
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider"
                    color={labelColor} mb={4}>Núcleos</Text>

                <Grid templateColumns={{ base: '1fr', md: '1fr auto 1fr' }} gap={4} alignItems="flex-end">
                    <Box>
                        <FieldLabel required>Origem</FieldLabel>
                        <Select size="md" value={fromId}
                            onChange={e => handleFromChange(e.target.value)}
                            isInvalid={!!errors.from}
                        >
                            {warehouseOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Select>
                        {errors.from && <Text fontSize="2xs" color="red.500" mt={0.5}>{errors.from}</Text>}
                    </Box>

                    <Flex align="center" justify="center" pb={1}>
                        <Flex w={9} h={9} borderRadius="full"
                            bg="teal.50" _dark={{ bg: 'teal.900' }}
                            align="center" justify="center">
                            <Icon name="right" boxSize={5} color="teal.500" />
                        </Flex>
                    </Flex>

                    <Box>
                        <FieldLabel required>Destino</FieldLabel>
                        <Select size="md" value={toId}
                            onChange={e => { setToId(e.target.value); setErrors(ev => ({ ...ev, to: null })) }}
                            isInvalid={!!errors.to}
                        >
                            {warehouseOptions.map(o => (
                                <option key={o.value} value={o.value}
                                    disabled={o.value && o.value === fromId}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                        {errors.to && <Text fontSize="2xs" color="red.500" mt={0.5}>{errors.to}</Text>}
                    </Box>
                </Grid>
            </Box>

            {/* Produtos */}
            <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" p={6} mb={5}>
                <Flex justify="space-between" align="center" mb={4}>
                    <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color={labelColor}>
                        Produtos a transferir
                    </Text>
                    <Button size="sm" colorScheme="teal" variant="solid" borderRadius="full"
                        leftIcon={<Icon name="new" boxSize={3.5} />}
                        onClick={addItem}
                    >
                        Adicionar produto
                    </Button>
                </Flex>

                <Flex direction="column" gap={3}>
                    {items.map((item, i) => (
                        <TransferItem
                            key={i}
                            index={i}
                            item={item}
                            fromId={fromId}
                            canRemove={items.length > 1}
                            onChange={patch => setItem(i, patch)}
                            onRemove={() => rmItem(i)}
                            errors={{
                                product: errors[`item_${i}_product`],
                                qty:     errors[`item_${i}_qty`],
                            }}
                        />
                    ))}
                </Flex>
            </Box>

            {/* Observações + Submit */}
            <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" p={6}>
                <Box mb={5}>
                    <FieldLabel>Observações</FieldLabel>
                    <Input size="md" as="textarea" rows={2}
                        value={notes} placeholder="Motivo da transferência, referência interna..."
                        onChange={e => setNotes(e.target.value)}
                        style={{ resize: 'vertical', minHeight: '56px', paddingTop: '8px' }}
                    />
                </Box>

                <Flex justify="flex-end" gap={3}>
                    <Button variant="ghost" onClick={() => navigate('/stock/movements')}>Cancelar</Button>
                    <Button colorScheme="teal" isLoading={loading}
                        leftIcon={<Icon name="right" boxSize={4} />}
                        loadingText="Transferindo..."
                        onClick={() => { if (validate()) authenticate.onOpen() }}
                        isDisabled={!fromId || !toId}
                    >
                        Confirmar Transferência
                    </Button>
                </Flex>
            </Box>

            <Authenticate {...authenticate} onSuccess={handleSubmit} />
        </Container>
    )
}
